import { useEffect } from 'react';
import DeleteIcon from '@mui/icons-material/Delete';
import type { ScaleRatingOptions } from 'types/forms';
import { Grid, Button, Typography } from '@mui/material';
import { getIn, setIn, useField, useFormikContext } from 'formik';

import { controlError } from 'utils/error';
import { RichText } from 'components/rich-text';
// Direct imports avoid initializing unrelated components through the barrel.
import { AddButton } from 'components/add-button';
import { InputField } from 'components/fields/input';
import { questionLabelText } from 'utils/question-label';
import { isSingleScaleRating } from 'utils/scale-rating';
import { RichTextField } from 'components/fields/rich-text';

import { FormScaleRatingFieldProps } from './types';
import { getAlignedAnswerValues } from './answer-values';

const EMPTY_ARRAY: string[] = [];

// The row label and matrix mode must be committed in a single Formik update.
const ScaleRatingRowInput = ({
  name,
  onChange,
  rowNumber,
}: {
  name: string;
  rowNumber: number;
  onChange: (value: string) => void;
}) => {
  const [field, meta, helpers] = useField<string>(name);
  const errorText = controlError(meta, name, 'Select option');
  return (
    <RichText
      editable
      acceptPlainText
      fieldName={name}
      allowImages={false}
      onChange={onChange}
      error={!!errorText}
      label="Select option"
      helperText={errorText}
      value={field.value ?? ''}
      placeholder="Enter a statement"
      onBlur={() => helpers.setTouched(true)}
      ariaLabel={`Select option ${rowNumber}`}
    />
  );
};

export const FormScaleRatingField = ({
  name,
  value,
  allowReferenceCodes = false,
}: FormScaleRatingFieldProps) => {
  const {
    values,
    validateForm,
    setFieldValue,
    setFormikState,
    validateOnChange,
  } = useFormikContext<Record<string, unknown>>();
  const items = value.options?.options;
  const rowsEnabled =
    value.options?._rowsEnabled ?? !isSingleScaleRating(items);
  const updateOptions = (patch: Partial<ScaleRatingOptions>) =>
    setFieldValue(`${name}.options`, { ...value.options, ...patch });
  // FieldArray shifts only one array; labels and codes need the same atomic update.
  // Validate after committing values and both arrays' errors/touched state.
  const updateRows = (
    patch: Partial<ScaleRatingOptions>,
    action?: number | 'reset'
  ) => {
    const nextValues = setIn(values, `${name}.options`, {
      ...value.options,
      ...patch,
    });
    const updateMeta = <T,>(meta: T): T => {
      if (action === undefined) return meta;
      let next = meta;
      for (const key of ['options', 'answerCodes']) {
        const path = `${name}.options.${key}`;
        const entries: unknown = getIn(next, path);
        if (action === 'reset' || typeof entries === 'string') {
          // Array-level schema errors need recomputing for the new row count.
          next = setIn(next, path, undefined);
        } else if (Array.isArray(entries)) {
          const remaining = entries.slice();
          remaining.splice(action, 1);
          next = setIn(next, path, remaining.length ? remaining : undefined);
        }
      }
      return next;
    };
    setFormikState((state) => ({
      ...state,
      values: nextValues,
      errors: updateMeta(state.errors),
      touched: updateMeta(state.touched),
    }));
    // setFormikState does not trigger Formik's usual change validation itself.
    if (validateOnChange) void validateForm(nextValues);
  };
  const setRowsEnabled = (enabled: boolean) =>
    updateRows(
      {
        options: [''],
        answerCodes: [],
        _rowsEnabled: enabled,
      },
      'reset'
    );

  const answerCodes = Array.from(
    { length: items?.length ?? 0 },
    (_, index) => value.options?.answerCodes?.[index] ?? ''
  );
  const addRow = () =>
    updateRows({
      _rowsEnabled: true,
      options: [...(items ?? []), ''],
      answerCodes: [...answerCodes, ''],
    });
  const removeRow = (index: number) =>
    updateRows(
      {
        _rowsEnabled: true,
        options: items?.filter((_, idx) => idx !== index),
        answerCodes: answerCodes.filter((_, idx) => idx !== index),
      },
      index
    );

  const minValue = value.options?.minValue ?? 1;
  const maxValue = value.options?.maxValue ?? 7;
  const validRange =
    Number.isInteger(minValue) &&
    Number.isInteger(maxValue) &&
    maxValue > minValue;
  const valueLabelsCount = validRange ? maxValue - minValue + 1 : 0;
  const currentValueLabels = value.options?.valueLabels ?? EMPTY_ARRAY;
  const valueLabelsSignature = currentValueLabels.join('\u0000');
  const updateRangeValues = (key: 'minValue' | 'maxValue', input: string) => {
    // Match InputField's number handling; ignore an unfinished range edit.
    const digits = input.replace(/[^0-9]/g, '');
    if (!digits) return;
    const nextValue = Number(digits);
    const nextMin = key === 'minValue' ? nextValue : minValue;
    const nextMax = key === 'maxValue' ? nextValue : maxValue;
    if (
      !Number.isInteger(nextMin) ||
      !Number.isInteger(nextMax) ||
      nextMax <= nextMin
    )
      return;
    updateOptions({
      [key]: nextValue,
      answerValues: getAlignedAnswerValues(
        value.options?.answerValues ?? [],
        nextMax - nextMin + 1
      ),
    });
  };

  useEffect(() => {
    // Clearing a range input while typing must not erase existing labels or values.
    if (!validRange) return;
    const normalizedLabels = Array.from(
      { length: valueLabelsCount },
      (_, i) => currentValueLabels[i] ?? ''
    );
    const isSame =
      normalizedLabels.length === currentValueLabels.length &&
      normalizedLabels.every(
        (label, index) => label === currentValueLabels[index]
      );

    if (!isSame) {
      setFieldValue(`${name}.options.valueLabels`, normalizedLabels, false);
    }
  }, [validRange, valueLabelsCount, valueLabelsSignature, name, setFieldValue]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-4">
        <Grid container spacing={2}>
          <Grid size={{ md: 6, xs: 12 }}>
            <div className="flex flex-col gap-4">
              <InputField
                fullWidth
                type="number"
                label="Min value"
                placeholder="Min value"
                name={`${name}.options.minValue`}
                onChange={(event) =>
                  updateRangeValues('minValue', event.target.value)
                }
              />
            </div>
          </Grid>

          <Grid size={{ md: 6, xs: 12 }}>
            <div className="flex flex-col gap-4">
              <InputField
                fullWidth
                type="number"
                label="Max value"
                placeholder="Max value"
                name={`${name}.options.maxValue`}
                onChange={(event) =>
                  updateRangeValues('maxValue', event.target.value)
                }
              />
            </div>
          </Grid>
        </Grid>

        <Typography variant="h6">Scale Value Labels</Typography>

        {Array.from({ length: valueLabelsCount }, (_, index) => {
          const scaleValue = minValue + index;
          return (
            <Grid
              container
              spacing={2}
              key={scaleValue}
              className="items-center"
            >
              <Grid size={{ xs: 12, md: allowReferenceCodes ? 8 : 12 }}>
                <RichTextField
                  acceptPlainText
                  allowImages={false}
                  label={`Value ${scaleValue} label`}
                  name={`${name}.options.valueLabels.${index}`}
                  placeholder={`Enter a label for value ${scaleValue}`}
                />
              </Grid>
              {allowReferenceCodes && (
                <Grid size={{ md: 4, xs: 12 }}>
                  <InputField
                    fullWidth
                    label="Value"
                    placeholder={String(scaleValue)}
                    slotProps={{ inputLabel: { shrink: true } }}
                    name={`${name}.options.answerValues.${index}`}
                  />
                </Grid>
              )}
            </Grid>
          );
        })}

        <Typography variant="h6">Scale Rating Options (optional)</Typography>

        {rowsEnabled &&
          items &&
          items.map((_, idx) => (
            <Grid key={idx} container spacing={2} className="items-center">
              {/* With codes, the editor occupies its own row; code and delete share the next. */}
              <Grid size={{ xs: 12, md: allowReferenceCodes ? 12 : 11 }}>
                <ScaleRatingRowInput
                  rowNumber={idx + 1}
                  name={`${name}.options.options.${idx}`}
                  onChange={(text) => {
                    // Preserve matrix mode when its only row is cleared.
                    updateOptions({
                      _rowsEnabled: true,
                      options: items.map((item, index) =>
                        index === idx ? text : item
                      ),
                    });
                  }}
                />
              </Grid>

              {allowReferenceCodes && (
                <Grid size={{ md: 11, xs: 12 }}>
                  <InputField
                    fullWidth
                    label="Option code"
                    placeholder="Option code"
                    name={`${name}.options.answerCodes.${idx}`}
                  />
                </Grid>
              )}

              <Grid size={{ md: 1, xs: 12 }}>
                <Button
                  color="error"
                  sx={{ minWidth: 0 }}
                  onClick={() =>
                    items.length === 1 ? setRowsEnabled(false) : removeRow(idx)
                  }
                  aria-label={
                    questionLabelText(items[idx] ?? '').trim()
                      ? `Remove row: ${questionLabelText(items[idx] ?? '')}`
                      : `Remove row ${idx + 1}`
                  }
                >
                  <DeleteIcon />
                </Button>
              </Grid>
            </Grid>
          ))}

        {rowsEnabled ? (
          <AddButton title="row" onClick={addRow} />
        ) : (
          <Button className="self-start" onClick={() => setRowsEnabled(true)}>
            Add rows
          </Button>
        )}
      </div>
    </div>
  );
};
